import React, { useCallback, useEffect, useMemo, useState } from 'react';

import EnhancedButton from '@components/button/EnhancedButton';
import EnhancedModal from '@components/generic-modal/EnhancedModal';
import ListingTableWithPagination from '@components/table/with-pagination/ListingTableWithPagination';
import { IListingData } from '@models/listing';
import { certificatesHeaders } from '@features/production/policy/constants';
import { useGetCertificates } from '@features/production/policy/hooks';

import { makeStyles } from 'tss-react/mui';

const ACTIVE_CERTIFICATE_STATUSES = ['Bound', 'Inforce'];

const useStyles = makeStyles()(() => ({
  tableContainer: {
    marginTop: '8px'
  },
  buttonsContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    marginTop: '24px'
  }
}));

type Props = {
  open: boolean;
  policyId: string;
  lineCode: string;
  currencySymbol: string;
  onClose: () => void;
  onSubmit: (selectedCertificateIds: string[]) => void;
  submitting?: boolean;
  singleSelect?: boolean;
};

const AmendmentCertificatesPopupAtom: React.FC<Props> = ({
  open,
  policyId,
  lineCode,
  currencySymbol,
  onClose,
  onSubmit,
  submitting = false,
  singleSelect = false
}) => {
  const { classes } = useStyles();
  const [selectedCertificateIds, setSelectedCertificateIds] = useState<
    string[]
  >([]);

  useEffect(() => {
    if (open) {
      setSelectedCertificateIds([]);
    }
  }, [open]);

  const defaultSorting = useMemo(
    () => ({
      pageNumber: 1,
      searchKey: '',
      pageSize: 5,
      order: 'asc' as const,
      orderBy: 'certificateNumber'
    }),
    []
  );

  const { fetchData } = useGetCertificates(lineCode, policyId, defaultSorting, {
    statuses: ACTIVE_CERTIFICATE_STATUSES,
    keyByField: 'certificateID'
  });

  const [tableData, setTableData] = useState<IListingData>({
    pagedItems: {},
    pageNumber: 1,
    pageSize: 5,
    totalCount: 0
  });

  const headers = useMemo(
    () =>
      certificatesHeaders(currencySymbol, lineCode, {
        openCertificateInNewTab: true
      }),
    [currencySymbol, lineCode]
  );

  const onPageChange = useCallback(
    async (
      page: number,
      searchKey?: string,
      rowsPerPage?: number,
      order?: 'desc' | 'asc',
      orderBy?: string
    ) => {
      const nextTableData = await fetchData(
        page,
        searchKey,
        rowsPerPage,
        order,
        orderBy
      );
      setTableData(nextTableData);
    },
    [fetchData]
  );

  const handleClose = () => {
    setSelectedCertificateIds([]);
    onClose();
  };

  const handleSubmit = () => {
    onSubmit(selectedCertificateIds);
  };

  return (
    <EnhancedModal open={open} blur onClose={handleClose} title="Certificates">
      <div className={classes.tableContainer}>
        <ListingTableWithPagination
          key={open ? `amendment-certificates-${singleSelect ? 'single' : 'multi'}` : 'closed'}
          name="amendmentCertificates"
          headers={headers}
          data={tableData}
          onPageChange={onPageChange}
          defaultOrderByColumn={defaultSorting.orderBy}
          orderByAscendingByDefault
          inlineTitle="Certificates"
          inline
          pageSizeOptions={[5, 10, 25]}
          onSelectionChange={setSelectedCertificateIds}
          forceDisableSelectAll={singleSelect}
          singleSelect={singleSelect}
        />
      </div>
      <div className={classes.buttonsContainer}>
        <EnhancedButton onClick={handleClose} disabled={submitting}>
          Cancel
        </EnhancedButton>
        <EnhancedButton
          isPrimary
          onClick={handleSubmit}
          disabled={
            submitting ||
            selectedCertificateIds.length === 0 ||
            (singleSelect && selectedCertificateIds.length !== 1)
          }
        >
          Submit
        </EnhancedButton>
      </div>
    </EnhancedModal>
  );
};

export { AmendmentCertificatesPopupAtom, ACTIVE_CERTIFICATE_STATUSES };
